import type { ICellRendererParams } from 'ag-grid-community';
import { StatusBadge, type BadgeTone } from '../common/StatusBadge';

/**
 * 상태 뱃지 셀
 * - value 가 'Y'/'N' 또는 'active'/'inactive' 같은 케이스에서 활용
 */
export interface BadgeCellOptions {
  /** value → tone 매핑 */
  toneMap: Record<string, BadgeTone>;
  /** value → 표시 라벨 매핑 (기본 value 자체) */
  labelMap?: Record<string, string>;
  /** 기본 tone (매핑 없을 때) */
  defaultTone?: BadgeTone;
}

export function makeBadgeCell(options: BadgeCellOptions) {
  const { toneMap, labelMap, defaultTone = 'neutral' } = options;
  return function BadgeCell(params: ICellRendererParams) {
    const raw = params.value;
    if (raw == null) return '-';
    const key = String(raw);
    const tone = toneMap[key] ?? defaultTone;
    const label = labelMap?.[key] ?? key;
    return <StatusBadge tone={tone}>{label}</StatusBadge>;
  };
}

/**
 * 날짜 포맷 셀
 */
export function DateCell(params: ICellRendererParams) {
  if (!params.value) return '-';
  try {
    return new Date(params.value).toLocaleDateString('ko-KR');
  } catch {
    return params.value;
  }
}

export function DateTimeCell(params: ICellRendererParams) {
  if (!params.value) return '-';
  try {
    return new Date(params.value).toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return params.value;
  }
}

/**
 * 금액 포맷 (원)
 */
export function CurrencyCell(params: ICellRendererParams) {
  const n = Number(params.value);
  if (Number.isNaN(n)) return '-';
  return `${n.toLocaleString()}원`;
}

/**
 * 숫자 포맷
 */
export function NumberCell(params: ICellRendererParams) {
  const n = Number(params.value);
  if (Number.isNaN(n)) return '-';
  return n.toLocaleString();
}

/**
 * 행 번호 (서버 페이지네이션과 함께 사용)
 * - 외부에서 startIndex 를 ColDef.cellRendererParams 로 전달
 */
export function RowNumberCell(
  params: ICellRendererParams & { startIndex?: number },
) {
  const start = params.startIndex ?? 0;
  const idx = params.node.rowIndex ?? 0;
  return start + idx + 1;
}

/**
 * 액션 버튼 셀 빌더
 * - 사용 예: cellRenderer: makeActionCell([{ label: '상세', onClick: (row) => navigate(...) }])
 */
export interface ActionButton<T> {
  label: string;
  onClick: (row: T) => void;
  /** 'default' | 'primary' | 'danger' */
  variant?: 'default' | 'primary' | 'danger';
  /** 조건부 노출 */
  show?: (row: T) => boolean;
  /** 조건부 비활성화 */
  disabled?: (row: T) => boolean;
}

const variantClasses = {
  default:
    'border-gray-300 bg-white text-gray-700 hover:bg-gray-50',
  primary:
    'border-primary-500 bg-primary-500 text-white hover:bg-primary-600',
  danger:
    'border-red-300 bg-white text-red-600 hover:bg-red-50',
};

export function makeActionCell<T>(buttons: ActionButton<T>[]) {
  return function ActionCell(params: ICellRendererParams<T>) {
    const row = params.data;
    if (!row) return null;
    return (
      <div className="flex h-full items-center gap-1">
        {buttons
          .filter(b => !b.show || b.show(row))
          .map((b, i) => {
            const disabled = b.disabled?.(row) ?? false;
            return (
              <button
                key={i}
                type="button"
                disabled={disabled}
                onClick={e => {
                  e.stopPropagation();
                  b.onClick(row);
                }}
                className={`rounded-md border px-2.5 py-1 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                  variantClasses[b.variant ?? 'default']
                }`}
              >
                {b.label}
              </button>
            );
          })}
      </div>
    );
  };
}

/**
 * 텍스트 셀 (말줄임 + tooltip)
 */
export function TruncatedTextCell(params: ICellRendererParams) {
  const text = params.value ?? '-';
  return (
    <span className="truncate" title={String(text)}>
      {text}
    </span>
  );
}
